import {
  IXeroInfo,
  IXeroConnectionHistory,
  IXeroConnectionHistoryResp,
  QueryParams,
} from 'hooks/api/useTransactions';
import { ApiListResp } from 'models/api';
import queryString from 'query-string';
import { defaultPageInfo } from './account';
import { apiV2 } from './axios';

export const UNKNOWN_TRANSACTION_CATEGORY_CODE = '6999';
export const UNKNOWN_TRANSACTION_CATEGORY = 'uncategorized'; // Specifically for the unknown transactions page query

export interface IProfitLossGraphData {
  id: string;
  accountId: string;
  businessId: string;
  organizationId: string;
  year: number;
  month: number;
  grossProfit: number;
  netProfit: number;
  expense: number;
  wages: number;
  updatedAt: Date;
  // eslint-disable-next-line camelcase
  total_income: number;
}

export interface ISaveXeroCodeProps {
  code: string;
  redirectUrl: string;
}

export interface ICategoryItem {
  accountID: string;
  businessID: string;
  organizationID: string;
  addToWatchlist: boolean;
  bankAccountNumber: string;
  bankAccountType: string;
  class: string;
  code: string;
  systemAccount: string;
  currencyCode: string;
  enablePaymentsToAccount: boolean;
  formAccountID: string;
  hasAttachments: boolean;
  id: string;
  name: string;
  reportingCode: string;
  reportingCodeName: string;
  showInExpenseClaims: boolean;
  status: string;
  taxType: string;
  type: string;
  updatedDateUTC: Date;
}

export interface IXeroTransactionNote {
  note?: string;
  lastUpdatedAt: string;
}

export interface IXeroTransactionItem {
  id: string;
  accountId: string;
  businessId: string;
  organizationId: string;
  type: string;
  amount: number;
  currency: string;
  contact: string;
  description: string;
  date: string;
  source: string;
  categoryCode: string;
  note: IXeroTransactionNote;
  // eslint-disable-next-line camelcase
  is_reconciled: boolean;
}

export const saveXeroCodeToServer = async (
  businessId: string,
  params: ISaveXeroCodeProps,
) => apiV2.post(`xero/${businessId}`, params);

export interface ITransactionListItemResp {
  data: IXeroTransactionItem[];
}

export type ApiListTransactions = ApiListResp<IXeroTransactionItem[]>;

export const getXeroTransactions = async (
  businessId: string,
  params: Partial<QueryParams>,
): Promise<ApiListTransactions> => {
  const { data } = await apiV2.get<ApiListTransactions>(
    `xero/${businessId}/transactions?${queryString.stringify({ ...params })}`,
  );
  return {
    data: data?.data || [],
    pageInfo: data?.pageInfo || defaultPageInfo,
  };
};

export const getXeroConnectionStatus = async (
  businessId: string,
): Promise<IXeroInfo> => {
  const { data } = await apiV2.get<IXeroInfo>(`xero/${businessId}`);
  return data;
};

const transformConnectionResponse = (
  data: IXeroConnectionHistoryResp[],
) =>
  data.map((item) => ({
    id: item.id,
    createdAt: item.created_at,
    createdBy: item.created_by,
    xeroAccountId: item.xero_account_id,
    xeroCompanyName: item.xero_company_name,
    xeroErrors: item.xero_errors ?? null,
    operation: item.operation,
    accountId: item.account_id,
    businessId: item.business_id,
    organizationId: item.organization_id,
  }));

export const getXeroConnectionHistory = async (
  businessId: string,
): Promise<IXeroConnectionHistory[]> =>
  apiV2
    .get<IXeroConnectionHistoryResp[]>(
      `xero/${businessId}/connection-history`,
    )
    .then((resp) => transformConnectionResponse(resp.data));

export const getXeroCategories = async (
  businessId: string,
): Promise<ICategoryItem[]> => {
  const { data } = await apiV2.get<ICategoryItem[]>(
    `xero/${businessId}/accounts`,
  );
  return data;
};

export const updateTransaction = async (
  businessId: string,
  transactionId: string,
  params: {
    categoryCode?: string;
    note?: string;
  },
): Promise<any> => {
  const { data } = await apiV2.patch(
    `xero/${businessId}/transactions/${transactionId}`,
    {
      ...params,
    },
  );
  return data;
};

export const disconnectXero = async (
  businessId: string,
  force: boolean = false,
) => {
  const params = force ? { force } : {};
  const { data } = await apiV2.delete(`xero/${businessId}`, {
    params,
  });
  return data;
};

export const createTransaction = async (
  businessId: string,
  formData: FormData,
) => {
  const { data } = await apiV2.post<FormData>(
    `xero/${businessId}/transactions`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    },
  );
  return data;
};

export const getProfitLossGraphData = async (businessId: string) => {
  const { data } = await apiV2.get<IProfitLossGraphData[]>(
    `xero/${businessId}/profit-loss`,
  );
  // due to different country setting in XERO, sometime expense would be return as negative number, use Math.abs to unify the data
  return data.map((item) => ({
    ...item,
    expense: Math.abs(item.expense),
  }));
};

export const deleteAllTransactions = (businessId: string) =>
  apiV2.delete(`xero/${businessId}/data`);

export const getXeroUnknownTransactionCount = async (
  businessId: string,
  year: number,
): Promise<{ count: number }> => {
  const { data } = await apiV2.get<{ count: number }>(
    `xero/${businessId}/count-unknown`,
    {
      params: {
        year,
      },
    },
  );
  return data;
};
